import { Alert, Group, Stack, useMantineColorScheme } from '@mantine/core';
import { Button } from '@repo/foundations';
import {
	AddressElement,
	Elements,
	PaymentElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { useCallback, useState } from 'react';
import { closeAllModals } from '../ModalManager';

interface StripePaymentMethodModalProps {
	clientSecret: string;
	stripeInstance: Promise<Stripe | null>;
	onSuccess?: () => void;
	onCancel?: () => void;
	type: 'setup_intent' | 'payment_intent';
}

function PaymentForm({
	onSuccess,
	onCancel,
	type,
}: Pick<StripePaymentMethodModalProps, 'onSuccess' | 'onCancel' | 'type'>) {
	const stripe = useStripe();
	const elements = useElements();
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = useCallback(
		async (event: React.FormEvent) => {
			event.preventDefault();

			if (!stripe || !elements) {
				return;
			}

			setIsProcessing(true);
			setError(null);

			const { error: submitError } = await stripe[
				type === 'setup_intent' ? 'confirmSetup' : 'confirmPayment'
			]({
				elements,
				confirmParams: {
					return_url: window.location.href,
				},
			});

			if (submitError) {
				setError(
					submitError.message ||
						'An error occurred while updating your payment method.'
				);
				setIsProcessing(false);
			} else {
				onSuccess?.();
				closeAllModals();
			}
		},
		[stripe, elements, type, onSuccess]
	);

	return (
		<form onSubmit={handleSubmit}>
			<Stack spacing="md">
				{error && (
					<Alert color="red" title="Error">
						{error}
					</Alert>
				)}
				<AddressElement
					options={{
						mode: 'billing',
						fields: {
							phone: 'auto',
						},
					}}
				/>
				<PaymentElement />
				<Group align="flex-end" w="100%" position="right">
					<Button
						variant="tertiary"
						onClick={onCancel}
						disabled={isProcessing}
						mt="xs"
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						type="submit"
						disabled={!stripe || isProcessing}
						loading={isProcessing}
					>
						{isProcessing ? 'Updating...' : 'Submit payment method'}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}

export function StripePaymentMethodModal({
	clientSecret,
	stripeInstance,
	onSuccess,
	onCancel,
	type,
}: StripePaymentMethodModalProps) {
	const { colorScheme } = useMantineColorScheme();
	if (!stripeInstance) {
		return (
			<Stack spacing="md">
				<Alert color="red" title="Error">
					Stripe configuration is not available.
				</Alert>
				<Button onClick={onCancel}>Close</Button>
			</Stack>
		);
	}

	return (
		<Elements
			stripe={stripeInstance}
			options={{
				clientSecret,
				appearance: {
					theme: colorScheme === 'dark' ? 'night' : 'stripe',
				},
			}}
		>
			<PaymentForm onSuccess={onSuccess} onCancel={onCancel} type={type} />
		</Elements>
	);
}
